import { Shield, Clock, CreditCard, AlertCircle, Mail, Phone, CheckCircle, XCircle } from "lucide-react";
import { motion } from "framer-motion";

const RefundPolicy = () => {
  const refundRules = [
    { icon: <XCircle className="w-5 h-5" />, title: "No Refund After Trip Starts", description: "Once the trip has started, cancellations will not be considered, and the full rental amount must be paid.", accent: "red" },
    { icon: <Clock className="w-5 h-5" />, title: "2 Hour Window", description: "Cancellations made at least 2 hours before the trip start time will be eligible for a refund.", accent: "green" },
    { icon: <AlertCircle className="w-5 h-5" />, title: "Last Minute Cancellations", description: "No refunds will be issued for cancellations made less than 2 hours before the trip starts.", accent: "orange" },
    { icon: <CreditCard className="w-5 h-5" />, title: "Processing Time", description: "Refunds for eligible cancellations will be processed within 5-7 business days.", accent: "orange" },
    { icon: <XCircle className="w-5 h-5" />, title: "No-Show Policy", description: "If the bike is not picked up or delivered as per the booking, no refund will be provided.", accent: "red" },
    { icon: <Shield className="w-5 h-5" />, title: "Original Payment Method", description: "Refunds will only be issued to the original payment method used during booking.", accent: "orange" },
    { icon: <AlertCircle className="w-5 h-5" />, title: "Terms Violation", description: "If the customer violates the terms of use, the booking will be canceled without a refund.", accent: "orange" },
    { icon: <Shield className="w-5 h-5" />, title: "Policy Modifications", description: "The company reserves the right to modify the refund and cancellation policy without prior notice.", accent: "orange" },
  ];

  const refundTimeline = [
    { time: "72+ Hours Before", refund: "90% Refund", note: "10% processing fee", positive: true },
    { time: "24-72 Hours Before", refund: "50% Refund", note: "Partial refund", positive: true },
    { time: "6-24 Hours Before", refund: "25% Refund", note: "Limited refund", positive: false },
    { time: "2-6 Hours Before", refund: "No Refund", note: "No refund available", positive: false },
    { time: "After Trip Starts", refund: "No Refund", note: "Full payment required", positive: false },
  ];

  const steps = [
    { num: "1", title: "Contact Support", desc: "Reach out via email or phone with your booking details and cancellation request." },
    { num: "2", title: "Verification", desc: "Our team will verify your booking, check eligibility, and process your cancellation request." },
    { num: "3", title: "Receive Refund", desc: "If eligible, your refund will be processed to your original payment method within 5-7 business days." },
  ];

  const keyPoints = [
    { label: "Advance Notice", text: "Cancel at least 2 hours before your scheduled pickup time to be eligible for a refund." },
    { label: "Processing Time", text: "All eligible refunds are processed within 5-7 business days to the original payment method." },
    { label: "No-Show", text: "Failure to pick up the bike as scheduled results in no refund. Please inform us if you'll be late." },
    { label: "Terms Compliance", text: "Violating our terms of service may result in booking cancellation without any refund." },
    { label: "Policy Updates", text: "We reserve the right to modify this policy. Check our website for the latest version." },
  ];

  const container = { hidden: {}, show: { transition: { staggerChildren: 0.05 } } };
  const cardItem = { hidden: { opacity: 0, y: 16 }, show: { opacity: 1, y: 0, transition: { duration: 0.4 } } };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Hero */}
      <div className="relative bg-[#111111] border-b border-white/[0.08] py-16 sm:py-20 px-4 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: `linear-gradient(#FF6B35 1px, transparent 1px), linear-gradient(90deg, #FF6B35 1px, transparent 1px)`, backgroundSize: "50px 50px" }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-[#FF6B35] rounded-full filter blur-[120px] opacity-5 pointer-events-none" />
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="relative max-w-4xl mx-auto text-center">
          <div className="w-14 h-14 bg-[#FF6B35]/10 border border-[#FF6B35]/20 rounded-2xl flex items-center justify-center mx-auto mb-5">
            <Shield className="w-7 h-7 text-[#FF6B35]" />
          </div>
          <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#FF6B35]/10 border border-[#FF6B35]/20 rounded-full text-[#FF6B35] text-xs font-bold uppercase tracking-widest mb-4">Policy</span>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-black text-white mb-4 tracking-tight">Refund & Cancellation Policy</h1>
          <p className="text-base sm:text-lg text-gray-400">Clear and transparent terms for cancellations and refunds at OkMechanic</p>
        </motion.div>
      </div>

      {/* Notice */}
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="flex items-start gap-4 p-5 bg-[#FF6B35]/10 border border-[#FF6B35]/20 rounded-2xl">
          <AlertCircle className="w-5 h-5 text-[#FF6B35] flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-sm font-bold text-white mb-1">Important Information</h3>
            <p className="text-sm text-gray-400 leading-relaxed">Please read this policy carefully before making a booking. By completing your reservation, you acknowledge and agree to these terms.</p>
          </div>
        </div>
      </div>

      {/* Refund Timeline */}
      <div className="max-w-5xl mx-auto px-4 py-8">
        <h2 className="text-2xl font-black text-white text-center mb-2">Refund Timeline</h2>
        <p className="text-center text-gray-500 text-sm mb-6">Understanding when and how much you can get refunded</p>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {refundTimeline.map((item, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.07 }}
              className={`bg-[#111111] border rounded-2xl p-4 text-center ${item.positive ? "border-green-500/20" : "border-red-500/20"}`}>
              <div className={`text-xs font-medium mb-1 ${item.positive ? "text-green-400" : "text-red-400"}`}>{item.time}</div>
              <div className="text-base font-black text-white mb-1">{item.refund}</div>
              <div className="text-[10px] text-gray-600">{item.note}</div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Rules */}
      <div className="max-w-5xl mx-auto px-4 py-8">
        <h2 className="text-2xl font-black text-white text-center mb-2">Eligibility for Refunds</h2>
        <p className="text-center text-gray-500 text-sm mb-6">Detailed rules and conditions for refund eligibility</p>
        <motion.div variants={container} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-50px" }}
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
          {refundRules.map((rule, i) => (
            <motion.div key={i} variants={cardItem}
              className="bg-[#111111] border border-white/[0.08] hover:border-[#FF6B35]/20 rounded-2xl p-4 transition-colors">
              <div className="w-9 h-9 bg-[#FF6B35]/10 border border-[#FF6B35]/20 rounded-xl flex items-center justify-center mb-3 text-[#FF6B35]">
                {rule.icon}
              </div>
              <h3 className="text-sm font-bold text-white mb-2">{rule.title}</h3>
              <p className="text-xs text-gray-500 leading-relaxed">{rule.description}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* How to Request */}
      <div className="max-w-5xl mx-auto px-4 py-8">
        <h2 className="text-2xl font-black text-white text-center mb-8">How to Request a Refund</h2>
        <div className="grid md:grid-cols-3 gap-4">
          {steps.map((step, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
              className="bg-[#111111] border border-white/[0.08] rounded-2xl p-6 text-center">
              <div className="w-12 h-12 bg-[#FF6B35] rounded-2xl flex items-center justify-center mx-auto mb-4 text-white font-black text-lg shadow-lg shadow-[#FF6B35]/20">
                {step.num}
              </div>
              <h3 className="font-bold text-white mb-2">{step.title}</h3>
              <p className="text-sm text-gray-500 leading-relaxed">{step.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Key Points */}
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="bg-[#111111] border border-white/[0.08] rounded-2xl p-6 sm:p-8">
          <h2 className="text-lg font-bold text-white mb-5">Key Points to Remember</h2>
          <div className="space-y-4">
            {keyPoints.map((point, i) => (
              <div key={i} className="flex items-start gap-3">
                <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-gray-400"><span className="font-semibold text-white">{point.label}:</span> {point.text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Additional Info */}
      <div className="max-w-5xl mx-auto px-4 py-6">
        <div className="bg-[#111111] border border-white/[0.08] rounded-2xl p-5">
          <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-[#FF6B35]" /> Additional Information
          </h3>
          <ul className="space-y-2 text-xs text-gray-500">
            {["Security deposits are always refundable if the vehicle is returned in good condition.", "For group bookings or long-term rentals, special cancellation terms may apply.", "In case of force majeure events, special consideration may be given.", "Partial cancellations are treated as modifications and subject to availability.", "This policy applies to all bookings made through our website, app, or in-person."].map((item, i) => (
              <li key={i} className="flex items-start gap-2"><span className="text-[#FF6B35]">•</span>{item}</li>
            ))}
          </ul>
        </div>
      </div>

      {/* Contact */}
      <div className="max-w-5xl mx-auto px-4 py-8 pb-16">
        <div className="bg-[#FF6B35]/10 border border-[#FF6B35]/20 rounded-2xl p-6">
          <h2 className="text-lg font-bold text-white text-center mb-6">Need Help with a Refund?</h2>
          <div className="grid sm:grid-cols-2 gap-3 max-w-xl mx-auto">
            <a href="tel:9923887090" className="flex items-center gap-3 p-4 bg-white/[0.04] border border-white/[0.08] hover:border-[#FF6B35]/20 text-gray-300 rounded-xl transition-colors">
              <Phone className="w-5 h-5 text-[#FF6B35] flex-shrink-0" />
              <div><div className="text-xs text-gray-500">Call Us</div><div className="font-semibold text-sm">9923887090</div></div>
            </a>
            <a href="mailto:okmechanics@gmail.com" className="flex items-center gap-3 p-4 bg-white/[0.04] border border-white/[0.08] hover:border-[#FF6B35]/20 text-gray-300 rounded-xl transition-colors">
              <Mail className="w-5 h-5 text-[#FF6B35] flex-shrink-0" />
              <div><div className="text-xs text-gray-500">Email Us</div><div className="font-semibold text-sm break-all">okmechanics@gmail.com</div></div>
            </a>
          </div>
          
        </div>
        <p className="text-center text-xs text-gray-600 mt-6">Last Updated: October 2025 · By booking you agree to this Refund Policy.</p>
      </div>
    </div>
  );
};

export default RefundPolicy;
